/**
 * Configure SSO Step 5 — metadata assist (no metadata yet, agent help).
 */
import type { WizardQuickOption } from './vysorWizardEngine';
import {
  buildAgentContextHeader,
  isUnsureInput,
  shouldDelegateToAgent,
  DYNAMIC_AI_REPLY_RULES,
} from './vysorWizardAssistCommon';

export type MetadataAssistStepOutcome =
  | { kind: 'pass' }
  | { kind: 'assist'; message: string; options: WizardQuickOption[] }
  | { kind: 'agent'; message: string; attachmentText?: string }
  | { kind: 'invalid'; hint: string };

export function buildMetadataAssistOptions(): WizardQuickOption[] {
  return [
    { id: 'wz_skip', label: 'Skip', value: '__skip__' },
    { id: 'wz_cancel', label: 'Cancel', value: '__cancel__' },
  ];
}

export function buildMetadataAgentContext(
  collected: Record<string, string>,
  userMessage: string,
  attachmentText?: string,
): string {
  const lines = [
    'User is on Configure SSO — metadata / notes step.',
    `Application: ${collected.application_name || 'unknown'} (ID: ${collected.application_id || 'pending'})`,
    `Environment: ${collected.instance_name || 'unknown'} (ID: ${collected.instance_id || 'pending'})`,
    `Protocol: ${collected.sso_type || 'unknown'}`,
    `IdP platform: ${collected.platform || collected.platform_id || 'unknown'}`,
    '',
    DYNAMIC_AI_REPLY_RULES,
    'They can Skip and add metadata later. Extract SAML/OIDC fields when they paste them. When confident, call create_sso_request.',
    '',
    `User: ${userMessage}`,
  ];
  const header = buildAgentContextHeader('Configure SSO — metadata step', lines);
  if (attachmentText?.trim()) {
    return `${header}\n[Reference material]\n${attachmentText.slice(0, 6000)}`;
  }
  return header;
}

export function resolveMetadataAssistStepInput(
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  collected: Record<string, string>,
  assistActive: boolean,
): MetadataAssistStepOutcome {
  const trimmed = rawAnswer.trim();

  if (optionId === 'wz_skip' || optionValue === '__skip__') {
    return { kind: 'pass' };
  }

  if (
    isUnsureInput(trimmed)
    || /no metadata|don't have|do not have|not yet|skip for now|later/i.test(trimmed)
  ) {
    return {
      kind: 'agent',
      message: buildMetadataAgentContext(
        collected,
        trimmed || 'I do not have metadata yet.',
      ),
    };
  }

  if (
    /^https?:\/\//i.test(trimmed)
    || trimmed.startsWith('<?xml')
    || /<(\w+:)?EntityDescriptor/i.test(trimmed)
    || (trimmed.startsWith('{') && /"issuer"|"authorization_endpoint"/i.test(trimmed))
  ) {
    return { kind: 'pass' };
  }

  if (shouldDelegateToAgent(trimmed, assistActive)) {
    return {
      kind: 'agent',
      message: buildMetadataAgentContext(collected, trimmed),
    };
  }

  if (trimmed.length >= 8) {
    return { kind: 'pass' };
  }

  if (trimmed) {
    return {
      kind: 'agent',
      message: buildMetadataAgentContext(collected, trimmed),
    };
  }

  return {
    kind: 'invalid',
    hint:
      'Paste a metadata URL/XML, add notes, choose **Skip**, or ask what to request from your vendor.',
  };
}
